import React from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Hourglass, ArrowUpRight } from "lucide-react";
import { ProgressRing } from "@/components/contracts/ProgressRing";
import { ReportSection, DeltaChip, Stat, Panel, Phase2Note } from "./ReportSection";
import { translateFr as tfr } from "@/i18n";

const DealCard = ({ icon: Icon, label, deal, tone, onOpen }) => (
  <button onClick={deal ? onOpen : undefined} disabled={!deal}
    className="flex-1 text-left rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 hover:border-white/[0.14] transition-colors disabled:hover:border-white/[0.06] group"
    data-testid={`deal-${label === "Deal le plus rapide" ? "fastest" : "longest"}`}>
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon className={`h-3.5 w-3.5 ${tone}`} /> {label}
      {deal && <ArrowUpRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-60 transition-opacity" />}
    </div>
    {deal ? (
      <>
        <p className="text-lg font-semibold mt-1.5 tabular-nums">{deal.days} j</p>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{deal.title}</p>
      </>
    ) : <p className="text-sm text-muted-foreground mt-2">—</p>}
  </button>
);

export const SectionNegotiation = ({ negotiation }) => {
  const navigate = useNavigate();
  const n = negotiation || {};
  return (
    <ReportSection number="03" title={tfr("Performance de négociation")}
      purpose={tfr("Durée, itérations et taux de réussite de vos négociations.")} testid="section-negotiation">
      <Panel>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex items-center gap-5 lg:pr-6 lg:border-r border-white/[0.05] shrink-0">
            <ProgressRing percent={n.successRate ?? 0} size={104} stroke={7}
              color={n.successRate == null ? "#71717A" : n.successRate >= 70 ? "#22C55E" : n.successRate >= 40 ? "#F5A524" : "#E5484D"}>
              <span className="text-xl font-semibold tabular-nums" data-testid="nego-success-rate">{n.successRate != null ? `${n.successRate}%` : "—"}</span>
            </ProgressRing>
            <div>
              <p className="text-sm font-medium">{tfr("Taux de réussite")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{tfr("contrats signés vs abandonnés")}</p>
              <div className="mt-2"><DeltaChip delta={n.signedDelta} suffix={tfr("signés vs période préc.")} testid="nego-delta" /></div>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-5">
            <Stat label={tfr("Contrats signés")} value={n.signedCount} testid="nego-signed" />
            <Stat label={tfr("Durée moyenne")} value={n.avgDurationDays != null ? `${n.avgDurationDays} j` : "—"}
              sub={tfr("création → signature")} testid="nego-duration" />
            <Stat label={tfr("Négociations abandonnées")} value={n.abandoned} testid="nego-abandoned" />
            <Stat label={tfr("Versions moy. / contrat")} value={n.avgVersions} testid="nego-versions" />
            <Stat label={tfr("Révisions moy. / contrat")} value={n.avgRevisions} testid="nego-revisions" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-5 border-t border-white/[0.05]">
          <DealCard icon={Zap} label={tfr("Deal le plus rapide")} deal={n.fastestDeal} tone="text-green-500"
            onOpen={() => navigate(`/contracts/${n.fastestDeal.id}`)} />
          <DealCard icon={Hourglass} label={tfr("Négociation la plus longue")} deal={n.longestDeal} tone="text-amber-400"
            onOpen={() => navigate(`/contracts/${n.longestDeal.id}`)} />
        </div>
        <div className="mt-4">
          <Phase2Note testid="nego-clauses-note">{n.clauses?.note}</Phase2Note>
        </div>
      </Panel>
    </ReportSection>
  );
};
